import { StandardOptionProps } from "ui/forms/Filter";

import api from "API";
import * as Params from "API/params";

import {
  GetSubmissionGroupParams,
  GetSubmissionGroups,
  GetSubmissionSlotParams,
  GetSubmissionSlots,
} from "../DataCollection/payloads";
import * as Tasks from "./payloads";
import { transformFileDownloadResponse } from "./transformers";

export const tasksEndpoint = `/hub/data-tasks/`;
export const taskFilesEndpoint = `/hub/data-tasks/`;

// Any variance-check mutation can flip an activity between resolved and
// unresolved, so it must refetch the per-activity statuses for that task.
// Callers that read the statuses on demand instead (file-based comments, which
// only need them when Next is clicked) opt out with `skipActivitiesRefetch`.
const varianceCheckActivitiesTag = (taskId: unknown) => [
  { type: "VarianceCheckActivities" as const, id: String(taskId) },
];
const invalidateVarianceCheckActivities = (
  _result: unknown,
  error: unknown,
  {
    taskId,
    skipActivitiesRefetch,
  }: { taskId?: unknown; skipActivitiesRefetch?: unknown }
) => (error || skipActivitiesRefetch ? [] : varianceCheckActivitiesTag(taskId));

// The submitted-data endpoint reports which client files an uploaded schema
// references (`isUsed`). That usage is written whenever a schema or the
// submitted files of the task change, so those mutations refetch it.
const cleaningTaskFilesTag = (taskId: unknown) => [
  { type: "CleaningTaskFiles" as const, id: String(taskId) },
];
const invalidateCleaningTaskFiles = (
  _result: unknown,
  error: unknown,
  { taskId }: { taskId?: unknown }
) => (error ? [] : cleaningTaskFilesTag(taskId));

const extendedEndpoints = api.injectEndpoints({
  endpoints: (builder) => ({
    tasks: builder.query<
      Tasks.GetTasks,
      Params.GenericParamMap<Tasks.GetTasksParams>
    >({
      query: (query) => {
        const params = {
          page: query.page,
          ordering: Params.toString(query.ordering),
          reporting_year: Params.toString(query.task_reporting_years),
          status: Params.toArray(query.status),
          submission_group: Params.toArray(query.submissionGroup),
          submission_slot: Params.toArray(query.submissionSlot),
          assignee: Params.toArray(query.assignee),
          organisation: Params.toArray(query.organisation),
          show_completed: query.show_completed === "show_complete",
        };

        return {
          url: tasksEndpoint,
          params,
        };
      },
    }),
    tasksByOrganisation: builder.query<
      Tasks.GetTasksByOrganisation,
      Params.GenericParamMap<Tasks.GetTasksByOrganisationParams>
    >({
      query: (query) => {
        const params = {
          page: query.page,
          filter_status: Params.toString(query.filterStatus),
          ordering: Params.toString(query.ordering),
          submission_group: Params.toList(query.submissionGroup),
        };

        return {
          url: `/organisations/${query.organisationId}/tasks/`,
          params,
        };
      },
    }),
    taskTotalCount: builder.query<
      Tasks.TaskTotals,
      Params.GenericParamMap<Tasks.GetTaskTotalsParams>
    >({
      query: (query) => {
        return {
          url: `/organisations/${query.organisationId}/tasks/totals/`,
          params: {
            submission_group: Params.toList(query.submissionGroup),
          },
        };
      },
    }),
    taskDetails: builder.query<Tasks.GetTask, Params.GenericParamMap>({
      query: (query) => {
        return {
          url: `/organisations/${query.organisationId}/data-tasks/${query.taskId}/`,
        };
      },
      keepUnusedDataFor: 0,
    }),
    taskFiles: builder.query<
      Tasks.GetClassificationTaskFiles,
      Params.GenericParamMap
    >({
      query: (query) => {
        return {
          url: `/organisations/${query.organisationId}/data-tasks/${query.taskId}/task-files/`,
        };
      },
      keepUnusedDataFor: 0,
    }),
    cleaningTaskFiles: builder.query<
      Tasks.GetTaskFiles,
      Params.GenericParamMap
    >({
      query: (query) => {
        return {
          url: `/organisations/${query.organisationId}/data-tasks/${query.taskId}/submitted-data/`,
        };
      },
      providesTags: (_result, _error, { taskId }) =>
        cleaningTaskFilesTag(taskId),
      keepUnusedDataFor: 0,
    }),
    taskFileDownload: builder.query<Tasks.SavedFile, Params.GenericParamMap>({
      query: ({ organisationId, fileId }) => {
        return {
          url: `/organisations/${organisationId}/files/${fileId}`,
        };
      },
    }),
    taskEntityMappingRows: builder.query<
      Tasks.EntityMappingRow[],
      Params.GenericParamMap
    >({
      query: ({ organisationId, taskId }) => {
        return {
          url: `/organisations/${organisationId}/data-tasks/${taskId}/entity-mappings`,
        };
      },
    }),
    taskParameterMappingOptions: builder.query<
      Tasks.ParameterMappingOption[],
      Params.GenericParamMap
    >({
      query: ({ organisationId, taskId }) => {
        return {
          url: `/organisations/${organisationId}/data-tasks/${taskId}/parameter-mapping-options`,
        };
      },
    }),
    taskParameterMappingComplete: builder.mutation<
      Tasks.RequestTaskUpdate,
      Tasks.RequestTaskParametersSubmitParams
    >({
      query: ({ organisationId, taskId, activities, autoProcess }) => {
        let params = {};

        if (autoProcess) {
          params = {
            auto: true,
          };
        }

        return {
          url: `/organisations/${organisationId}/data-tasks/${taskId}/map-parameters`,
          method: "POST",
          body: [...activities],
          params,
        };
      },
    }),
    taskMissingDataCheckCells: builder.query<
      Tasks.MissingDataCheckCellsData,
      Params.GenericParamMap
    >({
      query: ({ organisationId, taskId, activityId, methodologyId }) => {
        return {
          url: `/organisations/${organisationId}/data-tasks/${taskId}/missing-data-check-cells`,
          params: {
            activity: activityId,
            methodology: methodologyId,
          },
        };
      },
    }),
    taskMissingDataCheckCellsUpdate: builder.mutation<
      Tasks.MissingDataCheckCellsData,
      Tasks.RequestTaskMissingDataCheckCellsUpdateParams
    >({
      query: ({
        organisationId,
        taskId,
        activityId,
        methodologyId,
        rowNumber,
        note,
        actions,
      }) => {
        return {
          url: `/organisations/${organisationId}/data-tasks/${taskId}/missing-data-check-cells`,
          method: "PATCH",
          params: {
            activity: activityId,
            methodology: methodologyId,
          },
          body: {
            rowNumber,
            note,
            actions,
          },
        };
      },
    }),
    taskMissingDataCheckEntities: builder.query<
      Tasks.MissingDataCheckEntities,
      Params.GenericParamMap
    >({
      query: ({ organisationId, taskId }) => {
        return {
          url: `/organisations/${organisationId}/data-tasks/${taskId}/missing-data-check-entities`,
        };
      },
    }),
    taskMissingDataCheckEntitiesUpdate: builder.mutation<
      Tasks.MissingDataCheckCellsData,
      Tasks.RequestTaskMissingDataCheckEntitysUpdateParams
    >({
      query: ({
        organisationId,
        taskId,
        activityId,
        methodologyId,
        entities,
        entity,
        action,
        note,
        values,
      }) => {
        return {
          url: `/organisations/${organisationId}/data-tasks/${taskId}/missing-data-check-entities`,
          method: "PATCH",
          params: {
            activity: activityId,
            methodology: methodologyId,
          },
          body: {
            entities,
            action,
            note,
            values,
            entity,
          },
        };
      },
    }),
    taskUpdate: builder.mutation<
      Tasks.RequestTaskUpdate,
      Tasks.RequestTaskUpdateParams
    >({
      query: ({
        organisationId,
        taskId,
        task,
        files,
        classifiedData,
        userId,
        autoProcess,
        action,
      }) => {
        let body = {};
        let params = {};

        if (files) {
          body = {
            files,
          };
        } else if (classifiedData) {
          body = classifiedData;
        } else if (userId) {
          body = {
            assignee: userId,
          };
        }

        if (action) {
          body = { ...body, action };
        }

        if (autoProcess) {
          params = {
            auto: true,
          };
        }

        return {
          url: `/organisations/${organisationId}/data-tasks/${taskId}/${task}/`,
          method: "POST",
          body,
          params,
        };
      },
    }),
    taskVarianceChecks: builder.query<
      Tasks.VarianceChecksList,
      Params.GenericParamMap<Tasks.GetVarianceChecksParams>
    >({
      query: ({ organisationId, taskId, activityId, page }) => {
        return {
          url: `/organisations/${organisationId}/data-tasks/${taskId}/variance-checks`,
          params: { activity: activityId, page, page_size: 50 },
        };
      },
    }),
    taskVarianceCheckActivities: builder.query<
      Tasks.VarianceCheckActivity[],
      Params.GenericParamMap
    >({
      query: ({ organisationId, taskId }) => {
        return {
          url: `/organisations/${organisationId}/data-tasks/${taskId}/variance-check-activities/`,
        };
      },
      providesTags: (_result, _error, { taskId }) =>
        varianceCheckActivitiesTag(taskId),
    }),
    taskVarianceChecksUpdate: builder.mutation<
      Tasks.VarianceChecksList,
      Record<
        string,
        Params.GenericParam | Tasks.VarianceCheckMetadata | boolean
      >
    >({
      query: ({
        organisationId,
        taskId,
        activityId,
        methodologyId,
        action,
        entity,
        metadata,
        dimension,
        note,
        value,
        row_id,
        file_id,
        estimation_approach,
      }) => {
        return {
          url: `/organisations/${organisationId}/data-tasks/${taskId}/variance-checks`,
          method: "PATCH",
          params: {
            activity: activityId,
            methodology: methodologyId,
          },
          body: {
            action,
            entity,
            metadata,
            dimension,
            note,
            value,
            row_id,
            file_id,
            estimation_approach,
          },
        };
      },
      invalidatesTags: invalidateVarianceCheckActivities,
    }),
    taskVarianceCheckReplaceFile: builder.mutation<
      unknown,
      Params.GenericParamMap
    >({
      query: ({
        organisationId,
        taskId,
        existingFileId,
        newFileId,
        activityId,
        entity,
      }) => {
        return {
          url: `/organisations/${organisationId}/data-tasks/${taskId}/variance-checks`,
          method: "PATCH",
          params: activityId ? { activity: activityId } : undefined,
          body: {
            action: "replace_file",
            old_file_id: existingFileId,
            new_file_id: newFileId,
            ...(entity ? { entity } : {}),
          },
        };
      },
      invalidatesTags: invalidateVarianceCheckActivities,
    }),
    taskVarianceCheckAddFile: builder.mutation<unknown, Params.GenericParamMap>(
      {
        query: ({ organisationId, taskId, entity, fileId, activityId }) => {
          return {
            url: `/organisations/${organisationId}/data-tasks/${taskId}/variance-checks`,
            method: "PATCH",
            params: activityId ? { activity: activityId } : undefined,
            body: {
              action: "add_file",
              ...(entity ? { entity } : {}),
              file_id: fileId,
            },
          };
        },
        invalidatesTags: invalidateVarianceCheckActivities,
      }
    ),
    taskVarianceCheckUpdateSurvey: builder.mutation<
      unknown,
      Record<string, Params.GenericParam | Tasks.VarianceCheckMetadata>
    >({
      query: ({
        organisationId,
        taskId,
        entity,
        dimension,
        metadata,
        surveyData,
        activityId,
      }) => {
        return {
          url: `/organisations/${organisationId}/data-tasks/${taskId}/variance-checks`,
          method: "PATCH",
          params: {
            activity: activityId,
          },
          body: {
            action: "update_survey",
            entity,
            dimension,
            metadata,
            survey_data: surveyData,
          },
        };
      },
      invalidatesTags: invalidateVarianceCheckActivities,
    }),
    taskDownloadTemplate: builder.query<
      Tasks.GetDownloadFileData,
      Params.GenericParamMap
    >({
      query: ({ organisationId, taskId, activityId, methodologyId }) => ({
        url: `/organisations/${organisationId}/data-tasks/${taskId}/data-extraction-schema/`,
        params: {
          activity: activityId,
          methodology: methodologyId,
        },
        method: "GET",
        responseHandler: async (response: Response) => {
          return transformFileDownloadResponse(response);
        },
      }),
    }),
    taskUploadFile: builder.mutation<
      Tasks.SavedFile,
      { organisationId: string; file: File }
    >({
      query: ({ organisationId, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `/organisations/${organisationId}/files/`,
          method: "POST",
          body: formData,
        };
      },
    }),
    taskDownloadInputData: builder.query<
      Tasks.GetDownloadFileData,
      Params.GenericParamMap
    >({
      query: ({ organisationId, taskId, activityId, methodologyId }) => ({
        url: `/organisations/${organisationId}/data-tasks/${taskId}/dry-run-input/`,
        params: {
          activity: activityId,
          methodology: methodologyId,
        },
        method: "GET",
        responseHandler: async (response: Response) => {
          return transformFileDownloadResponse(response);
        },
      }),
    }),
    taskRunCalculationTest: builder.query<
      Tasks.GetCalculationTest,
      Params.GenericParamMap
    >({
      query: ({ organisationId, taskId, activityId, methodologyId }) => ({
        url: `/organisations/${organisationId}/data-tasks/${taskId}/trigger-dry-run/`,
        params: {
          activity: activityId,
          methodology: methodologyId,
        },
      }),
    }),
    taskAmendInputFile: builder.mutation<
      Tasks.SavedFile,
      {
        organisationId?: string;
        taskId?: string;
        activityId?: string;
        methodologyId?: string;
        file: File;
      }
    >({
      query: ({ organisationId, taskId, activityId, methodologyId, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `/organisations/${organisationId}/data-tasks/${taskId}/input-file/`,
          method: "PATCH",
          params: {
            activity: activityId,
            methodology: methodologyId,
          },
          body: formData,
        };
      },
      invalidatesTags: invalidateCleaningTaskFiles,
    }),
    classifySubmissionSlots: builder.query<
      GetSubmissionSlots,
      Params.GenericParamMap<GetSubmissionSlotParams>
    >({
      query: ({ organisationId, submission_group_id }) => ({
        url: `/organisations/${organisationId}/submission-groups/${submission_group_id}/submission-slots`,
        method: "GET",
      }),
    }),
    taskDownloadEmissionTemplate: builder.query<
      Tasks.GetDownloadFileData,
      Params.GenericParamMap
    >({
      query: ({ organisationId, taskId, activityId, methodologyId }) => ({
        url: `/organisations/${organisationId}/data-tasks/${taskId}/offline-emissions-template/`,
        params: {
          activity: activityId,
          methodology: methodologyId,
        },
        method: "GET",
        responseHandler: async (response: Response) => {
          return transformFileDownloadResponse(response);
        },
      }),
    }),
    taskUploadEmission: builder.mutation<
      Tasks.TaskUploadEmission,
      Params.GenericParamMap
    >({
      query: ({
        organisationId,
        taskId,
        activityId,
        methodologyId,
        fileId,
      }) => ({
        url: `/organisations/${organisationId}/data-tasks/${taskId}/offline-emissions/`,
        params: {
          activity: activityId,
          methodology: methodologyId,
        },
        method: "PATCH",
        body: {
          file_id: fileId,
        },
      }),
    }),
    taskReset: builder.mutation<Tasks.TaskBaseParams, Params.GenericParamMap>({
      query: ({ organisationId, taskId }) => ({
        url: `/organisations/${organisationId}/data-tasks/${taskId}/reset/`,
        method: "POST",
      }),
    }),
    submissionGroupsList: builder.query<
      GetSubmissionGroups,
      Params.GenericParamMap<GetSubmissionGroupParams>
    >({
      query: ({ organisationId, reporting_year, name, page, page_size }) => ({
        url: `/organisations/${organisationId}/submission-groups/`,
        method: "GET",
        params: {
          reporting_year,
          name,
          page,
          page_size,
        },
      }),
    }),
    submissionSlotsList: builder.query<
      GetSubmissionSlots,
      Params.GenericParamMap<GetSubmissionSlotParams>
    >({
      query: ({ organisationId, submission_group_id }) => {
        return {
          url: `/organisations/${organisationId}/submission-groups/${submission_group_id}/submission-slots`,
          method: "GET",
        };
      },
    }),
    taskTransferFile: builder.mutation<
      Tasks.RequestTaskUpdateMutation,
      Tasks.RequestTaskTransferFile
    >({
      query: ({ organisationId, taskId, fileDetails }) => ({
        url: `/organisations/${organisationId}/data-tasks/${taskId}/transfer-file`,
        method: "POST",
        body: fileDetails,
      }),
      invalidatesTags: invalidateCleaningTaskFiles,
    }),
    taskReturnToOperation: builder.mutation<
      Tasks.ReturnedOperationTask,
      Tasks.ReturnToOperationTask
    >({
      query: ({ organisationId, taskId, operation }) => ({
        url: `/organisations/${organisationId}/data-tasks/${taskId}/return-to/`,
        method: "POST",
        body: {
          operation,
        },
      }),
    }),
    taskSubmissionGroupsFilter: builder.query<
      StandardOptionProps["options"],
      Params.GenericParamMap
    >({
      query: ({ organisationId, reporting_year }) => ({
        url: `/organisations/${organisationId}/submission-groups/filter-options/`,
        params: {
          reporting_year: reporting_year,
        },
      }),
      keepUnusedDataFor: 60,
      transformResponse(original: Tasks.TaskSubmissionGroup) {
        return original.map(({ label, value }) => {
          return { label: label, value: value };
        });
      },
    }),
    taskSurveyDownload: builder.query<
      Tasks.SavedSurveyFile,
      Params.GenericParamMap
    >({
      query: ({ organisationId, operationId, fileName }) => {
        return {
          url: `/organisations/${organisationId}/operation-outputs/${operationId}/survey_file/`,
          params: {
            file_name: fileName,
          },
        };
      },
    }),
    taskUpdateActivityMethodologies: builder.mutation<
      Tasks.TaskActivity[],
      Tasks.ActivityMethodologyParams
    >({
      query: ({ organisationId, taskId, activityMethodologies }) => {
        return {
          url: `/organisations/${organisationId}/data-tasks/${taskId}/activity-methodologies`,
          method: "PATCH",
          body: { activity_methodologies: activityMethodologies },
        };
      },
      invalidatesTags: invalidateCleaningTaskFiles,
    }),

    taskIntegrationRefresh: builder.mutation<
      Tasks.TaskBaseParams,
      Params.GenericParamMap
    >({
      query: ({ organisationId, taskId }) => ({
        url: `/organisations/${organisationId}/data-tasks/${taskId}/refresh-integration/`,
        method: "POST",
      }),
    }),

    taskIntegrationReprocess: builder.mutation<
      Tasks.TaskBaseParams,
      Params.GenericParamMap
    >({
      query: ({ organisationId, taskId }) => ({
        url: `/organisations/${organisationId}/data-tasks/${taskId}/reprocess-integration/`,
        method: "POST",
      }),
      invalidatesTags: invalidateCleaningTaskFiles,
    }),

    taskMissingDataNewSchemaFile: builder.mutation<
      unknown,
      Tasks.MissingDataNewSchemaFile
    >({
      query: ({
        organisationId,
        taskId,
        activityId,
        methodologyId,
        fileId,
      }) => ({
        url: `/organisations/${organisationId}/data-tasks/${taskId}/missing-data-check`,
        method: "PATCH",
        body: {
          activity_id: activityId,
          methodology_id: methodologyId,
          file_id: fileId,
        },
      }),
    }),
  }),
});

export const {
  useTasksQuery,
  useTasksByOrganisationQuery,
  useTaskTotalCountQuery,
  useTaskDetailsQuery,
  useLazyTaskDetailsQuery,
  useTaskFilesQuery,
  useCleaningTaskFilesQuery,
  useLazyCleaningTaskFilesQuery,
  useTaskEntityMappingRowsQuery,
  useTaskParameterMappingOptionsQuery,
  useTaskParameterMappingCompleteMutation,
  useTaskMissingDataCheckCellsQuery,
  useTaskMissingDataCheckCellsUpdateMutation,
  useLazyTaskMissingDataCheckEntitiesQuery,
  useTaskMissingDataCheckEntitiesUpdateMutation,
  useTaskFileDownloadQuery,
  useLazyTaskFileDownloadQuery,
  useTaskUpdateMutation,
  useTaskVarianceChecksQuery,
  useTaskVarianceCheckActivitiesQuery,
  useLazyTaskVarianceCheckActivitiesQuery,
  useTaskVarianceChecksUpdateMutation,
  useTaskVarianceCheckReplaceFileMutation,
  useTaskVarianceCheckAddFileMutation,
  useTaskUploadFileMutation,
  useTaskVarianceCheckUpdateSurveyMutation,
  useLazyTaskDownloadTemplateQuery,
  useLazyTaskDownloadInputDataQuery,
  useLazyTaskRunCalculationTestQuery,
  useTaskAmendInputFileMutation,
  useClassifySubmissionSlotsQuery,
  useLazyTaskDownloadEmissionTemplateQuery,
  useTaskUploadEmissionMutation,
  useTaskResetMutation,
  useTaskReturnToOperationMutation,
  useLazySubmissionGroupsListQuery,
  useSubmissionGroupsListQuery,
  useSubmissionSlotsListQuery,
  useTaskTransferFileMutation,
  useTaskSubmissionGroupsFilterQuery,
  useLazyTaskSurveyDownloadQuery,
  useTaskUpdateActivityMethodologiesMutation,
  useTaskIntegrationRefreshMutation,
  useTaskIntegrationReprocessMutation,
  useTaskMissingDataNewSchemaFileMutation,
} = extendedEndpoints;
